import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, CheckCircle2, ChevronRight, ChevronDown, Search, X } from "lucide-react";
import { uploadBaseline, uploadFirmwareFile, patchFirmwareMetadata, getBaseline, getFirmwareFileInfo, getFirmwareBinUrl, getFirmwareReleaseNotesUrl, getAuthToken } from "../../lib/api";
import { useToast } from "../../contexts/ToastContext";
import { useAuth } from "../../contexts/AuthContext";

const MAX_BYTES = 50 * 1024 * 1024;

// ISO 3166-1 alpha-2 country list
const COUNTRIES = [
  { code: "AF", label: "Afghanistan" },
  { code: "AL", label: "Albania" },
  { code: "DZ", label: "Algeria" },
  { code: "AD", label: "Andorra" },
  { code: "AO", label: "Angola" },
  { code: "AG", label: "Antigua and Barbuda" },
  { code: "AR", label: "Argentina" },
  { code: "AM", label: "Armenia" },
  { code: "AU", label: "Australia" },
  { code: "AT", label: "Austria" },
  { code: "AZ", label: "Azerbaijan" },
  { code: "BS", label: "Bahamas" },
  { code: "BH", label: "Bahrain" },
  { code: "BD", label: "Bangladesh" },
  { code: "BB", label: "Barbados" },
  { code: "BY", label: "Belarus" },
  { code: "BE", label: "Belgium" },
  { code: "BZ", label: "Belize" },
  { code: "BJ", label: "Benin" },
  { code: "BT", label: "Bhutan" },
  { code: "BO", label: "Bolivia" },
  { code: "BA", label: "Bosnia and Herzegovina" },
  { code: "BW", label: "Botswana" },
  { code: "BR", label: "Brazil" },
  { code: "BN", label: "Brunei" },
  { code: "BG", label: "Bulgaria" },
  { code: "BF", label: "Burkina Faso" },
  { code: "BI", label: "Burundi" },
  { code: "CV", label: "Cabo Verde" },
  { code: "KH", label: "Cambodia" },
  { code: "CM", label: "Cameroon" },
  { code: "CA", label: "Canada" },
  { code: "CF", label: "Central African Republic" },
  { code: "TD", label: "Chad" },
  { code: "CL", label: "Chile" },
  { code: "CN", label: "China" },
  { code: "CO", label: "Colombia" },
  { code: "KM", label: "Comoros" },
  { code: "CG", label: "Congo" },
  { code: "CD", label: "Congo (DRC)" },
  { code: "CR", label: "Costa Rica" },
  { code: "CI", label: "Côte d'Ivoire" },
  { code: "HR", label: "Croatia" },
  { code: "CU", label: "Cuba" },
  { code: "CY", label: "Cyprus" },
  { code: "CZ", label: "Czechia" },
  { code: "DK", label: "Denmark" },
  { code: "DJ", label: "Djibouti" },
  { code: "DM", label: "Dominica" },
  { code: "DO", label: "Dominican Republic" },
  { code: "EC", label: "Ecuador" },
  { code: "EG", label: "Egypt" },
  { code: "SV", label: "El Salvador" },
  { code: "GQ", label: "Equatorial Guinea" },
  { code: "ER", label: "Eritrea" },
  { code: "EE", label: "Estonia" },
  { code: "SZ", label: "Eswatini" },
  { code: "ET", label: "Ethiopia" },
  { code: "FJ", label: "Fiji" },
  { code: "FI", label: "Finland" },
  { code: "FR", label: "France" },
  { code: "GA", label: "Gabon" },
  { code: "GM", label: "Gambia" },
  { code: "GE", label: "Georgia" },
  { code: "DE", label: "Germany" },
  { code: "GH", label: "Ghana" },
  { code: "GR", label: "Greece" },
  { code: "GD", label: "Grenada" },
  { code: "GT", label: "Guatemala" },
  { code: "GN", label: "Guinea" },
  { code: "GW", label: "Guinea-Bissau" },
  { code: "GY", label: "Guyana" },
  { code: "HT", label: "Haiti" },
  { code: "HN", label: "Honduras" },
  { code: "HU", label: "Hungary" },
  { code: "IS", label: "Iceland" },
  { code: "IN", label: "India" },
  { code: "ID", label: "Indonesia" },
  { code: "IR", label: "Iran" },
  { code: "IQ", label: "Iraq" },
  { code: "IE", label: "Ireland" },
  { code: "IL", label: "Israel" },
  { code: "IT", label: "Italy" },
  { code: "JM", label: "Jamaica" },
  { code: "JP", label: "Japan" },
  { code: "JO", label: "Jordan" },
  { code: "KZ", label: "Kazakhstan" },
  { code: "KE", label: "Kenya" },
  { code: "KI", label: "Kiribati" },
  { code: "KW", label: "Kuwait" },
  { code: "KG", label: "Kyrgyzstan" },
  { code: "LA", label: "Laos" },
  { code: "LV", label: "Latvia" },
  { code: "LB", label: "Lebanon" },
  { code: "LS", label: "Lesotho" },
  { code: "LR", label: "Liberia" },
  { code: "LY", label: "Libya" },
  { code: "LI", label: "Liechtenstein" },
  { code: "LT", label: "Lithuania" },
  { code: "LU", label: "Luxembourg" },
  { code: "MG", label: "Madagascar" },
  { code: "MW", label: "Malawi" },
  { code: "MY", label: "Malaysia" },
  { code: "MV", label: "Maldives" },
  { code: "ML", label: "Mali" },
  { code: "MT", label: "Malta" },
  { code: "MH", label: "Marshall Islands" },
  { code: "MR", label: "Mauritania" },
  { code: "MU", label: "Mauritius" },
  { code: "MX", label: "Mexico" },
  { code: "FM", label: "Micronesia" },
  { code: "MD", label: "Moldova" },
  { code: "MC", label: "Monaco" },
  { code: "MN", label: "Mongolia" },
  { code: "ME", label: "Montenegro" },
  { code: "MA", label: "Morocco" },
  { code: "MZ", label: "Mozambique" },
  { code: "MM", label: "Myanmar" },
  { code: "NA", label: "Namibia" },
  { code: "NR", label: "Nauru" },
  { code: "NP", label: "Nepal" },
  { code: "NL", label: "Netherlands" },
  { code: "NZ", label: "New Zealand" },
  { code: "NI", label: "Nicaragua" },
  { code: "NE", label: "Niger" },
  { code: "NG", label: "Nigeria" },
  { code: "NO", label: "Norway" },
  { code: "OM", label: "Oman" },
  { code: "PK", label: "Pakistan" },
  { code: "PW", label: "Palau" },
  { code: "PA", label: "Panama" },
  { code: "PG", label: "Papua New Guinea" },
  { code: "PY", label: "Paraguay" },
  { code: "PE", label: "Peru" },
  { code: "PH", label: "Philippines" },
  { code: "PL", label: "Poland" },
  { code: "PT", label: "Portugal" },
  { code: "QA", label: "Qatar" },
  { code: "RO", label: "Romania" },
  { code: "RU", label: "Russia" },
  { code: "RW", label: "Rwanda" },
  { code: "KN", label: "Saint Kitts and Nevis" },
  { code: "LC", label: "Saint Lucia" },
  { code: "VC", label: "Saint Vincent and the Grenadines" },
  { code: "WS", label: "Samoa" },
  { code: "SM", label: "San Marino" },
  { code: "ST", label: "São Tomé and Príncipe" },
  { code: "SA", label: "Saudi Arabia" },
  { code: "SN", label: "Senegal" },
  { code: "RS", label: "Serbia" },
  { code: "SC", label: "Seychelles" },
  { code: "SL", label: "Sierra Leone" },
  { code: "SG", label: "Singapore" },
  { code: "SK", label: "Slovakia" },
  { code: "SI", label: "Slovenia" },
  { code: "SB", label: "Solomon Islands" },
  { code: "SO", label: "Somalia" },
  { code: "ZA", label: "South Africa" },
  { code: "SS", label: "South Sudan" },
  { code: "ES", label: "Spain" },
  { code: "LK", label: "Sri Lanka" },
  { code: "SD", label: "Sudan" },
  { code: "SR", label: "Suriname" },
  { code: "SE", label: "Sweden" },
  { code: "CH", label: "Switzerland" },
  { code: "SY", label: "Syria" },
  { code: "TW", label: "Taiwan" },
  { code: "TJ", label: "Tajikistan" },
  { code: "TZ", label: "Tanzania" },
  { code: "TH", label: "Thailand" },
  { code: "TL", label: "Timor-Leste" },
  { code: "TG", label: "Togo" },
  { code: "TO", label: "Tonga" },
  { code: "TT", label: "Trinidad and Tobago" },
  { code: "TN", label: "Tunisia" },
  { code: "TR", label: "Turkey" },
  { code: "TM", label: "Turkmenistan" },
  { code: "TV", label: "Tuvalu" },
  { code: "UG", label: "Uganda" },
  { code: "UA", label: "Ukraine" },
  { code: "AE", label: "United Arab Emirates" },
  { code: "GB", label: "United Kingdom" },
  { code: "US", label: "United States" },
  { code: "UY", label: "Uruguay" },
  { code: "UZ", label: "Uzbekistan" },
  { code: "VU", label: "Vanuatu" },
  { code: "VE", label: "Venezuela" },
  { code: "VN", label: "Vietnam" },
  { code: "YE", label: "Yemen" },
  { code: "ZM", label: "Zambia" },
  { code: "ZW", label: "Zimbabwe" },
];

const COUNTRY_LABEL = Object.fromEntries(COUNTRIES.map(({ code, label }) => [code, label]));

// Multi-select country dropdown component
function CountryDropdown({ value, onChange, error }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef();

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filtered = search.trim()
    ? COUNTRIES.filter((c) =>
        c.label.toLowerCase().includes(search.toLowerCase()) ||
        c.code.toLowerCase().includes(search.toLowerCase())
      )
    : COUNTRIES;

  const toggle = (code) => {
    onChange(value.includes(code) ? value.filter((c) => c !== code) : [...value, code]);
  };

  const removeChip = (code, e) => {
    e.stopPropagation();
    onChange(value.filter((c) => c !== code));
  };

  return (
    <div ref={ref} className="relative">
      {/* Trigger */}
      <div
        onClick={() => setOpen((o) => !o)}
        className={`min-h-[42px] flex flex-wrap items-center gap-1.5 px-3 py-2 rounded-lg border cursor-pointer bg-white transition-colors ${
          error ? "border-red-400" : open ? "border-teal-500 ring-1 ring-teal-300" : "border-slate-200 hover:border-teal-300"
        }`}
      >
        {value.length === 0 ? (
          <span className="text-sm text-slate-400">Select countries…</span>
        ) : (
          value.map((code) => (
            <span key={code} className="flex items-center gap-1 bg-teal-50 border border-teal-200 text-teal-800 text-xs font-medium px-2 py-0.5 rounded-full">
              <span className="font-mono">{code}</span>
              <span className="text-teal-500">·</span>
              {COUNTRY_LABEL[code]}
              <button type="button" onClick={(e) => removeChip(code, e)} className="ml-0.5 text-teal-400 hover:text-teal-700">
                <X className="w-3 h-3" />
              </button>
            </span>
          ))
        )}
        <ChevronDown className={`w-4 h-4 text-slate-400 ml-auto shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </div>

      {/* Dropdown */}
      {open && (
        <div className="absolute z-30 mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden">
          {/* Search */}
          <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-100">
            <Search className="w-4 h-4 text-slate-400 shrink-0" />
            <input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search country or code…"
              className="flex-1 text-sm outline-none bg-transparent placeholder-slate-400"
            />
            {search && <button type="button" onClick={() => setSearch("")}><X className="w-3.5 h-3.5 text-slate-400" /></button>}
          </div>

          {/* Select all / Clear */}
          <div className="flex gap-3 px-3 py-1.5 border-b border-slate-100 text-xs">
            <button type="button" className="text-teal-600 hover:underline" onClick={() => onChange(COUNTRIES.map((c) => c.code))}>Select all</button>
            <button type="button" className="text-slate-400 hover:underline" onClick={() => onChange([])}>Clear</button>
            <span className="ml-auto text-slate-400">{value.length} selected</span>
          </div>

          {/* List */}
          <ul className="max-h-52 overflow-y-auto divide-y divide-slate-50">
            {filtered.length === 0 && (
              <li className="px-4 py-3 text-sm text-slate-400 text-center">No countries found</li>
            )}
            {filtered.map(({ code, label }) => {
              const checked = value.includes(code);
              return (
                <li
                  key={code}
                  onClick={() => toggle(code)}
                  className={`flex items-center gap-3 px-4 py-2 cursor-pointer text-sm transition-colors ${
                    checked ? "bg-teal-50" : "hover:bg-slate-50"
                  }`}
                >
                  <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-colors ${
                    checked ? "bg-teal-600 border-teal-600" : "border-slate-300"
                  }`}>
                    {checked && (
                      <svg viewBox="0 0 10 8" className="w-2.5 h-2" fill="none">
                        <path d="M1 4l3 3 5-6" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </span>
                  <span className="font-mono text-xs text-slate-400 w-7 shrink-0">{code}</span>
                  <span className={checked ? "font-medium text-teal-800" : "text-slate-700"}>{label}</span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

async function computeSha256(file) {
  const buffer = await file.arrayBuffer();
  const hashBuffer = await window.crypto.subtle.digest("SHA-256", buffer);
  return Array.from(new Uint8Array(hashBuffer))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

function formatBytes(bytes) {
  return (bytes / 1048576).toFixed(1) + " MB";
}

const STEPS = [
  { n: 1, label: "Upload" },
  { n: 2, label: "Metadata" },
  { n: 3, label: "Review / Sign" },
  { n: 4, label: "Publish" },
];

function StepIndicator({ current }) {
  return (
    <div className="flex items-center gap-0 mb-8">
      {STEPS.map((s, i) => (
        <React.Fragment key={s.n}>
          <div className="flex items-center gap-2">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${
                s.n === current
                  ? "bg-teal-800 text-white"
                  : s.n < current
                  ? "bg-teal-600 text-white"
                  : "border-2 border-slate-300 text-slate-400 bg-white"
              }`}
            >
              {s.n < current ? <CheckCircle2 className="w-4 h-4" /> : s.n}
            </div>
            <span
              className={`text-sm whitespace-nowrap ${
                s.n === current ? "font-bold text-slate-800" : "text-slate-400"
              }`}
            >
              {s.label}
            </span>
          </div>
          {i < STEPS.length - 1 && (
            <div className="flex-1 h-px bg-slate-200 mx-4 min-w-[40px]" />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

function StepUpload({ onNext, initialFile, initialSha256, initialFileInfo, initialFirmwareId }) {
  const inputRef = useRef();
  const firmwareIdRef = useRef(initialFirmwareId || null);
  const [dragOver, setDragOver] = useState(false);
  const [file, setFile] = useState(initialFile || null);
  const [sha256, setSha256] = useState(initialSha256 || null);
  const [computing, setComputing] = useState(false);
  const [readProgress, setReadProgress] = useState(0);
  const [progressLabel, setProgressLabel] = useState("");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadDone, setUploadDone] = useState(!!initialSha256);
  const [uploadError, setUploadError] = useState(null);
  const [error, setError] = useState(null);
  // Restored file display info when File object is unavailable after navigation
  const [restoredFileInfo, setRestoredFileInfo] = useState(initialFileInfo || null);

  const processFile = useCallback(async (f) => {
    setError(null);
    setUploadError(null);
    if (!f.name.endsWith(".bin")) {
      setError("Invalid file type. Only .bin files are accepted.");
      setFile(null);
      setSha256(null);
      return;
    }
    if (f.size > MAX_BYTES) {
      setError("File exceeds the 50 MB limit.");
      setFile(null);
      setSha256(null);
      return;
    }

    setFile(f);
    setComputing(true);
    setUploadDone(false);
    setReadProgress(0);
    setProgressLabel("Reading file...");

    await new Promise((r) => setTimeout(r, 50));

    // Phase 1: Time-based read progress (0 → 100)
    const DURATION_MS = 2000;
    const INTERVAL_MS = 40;
    const steps = DURATION_MS / INTERVAL_MS;
    let step = 0;
    const ticker = setInterval(() => {
      step += 1;
      const pct = Math.min(Math.round((step / steps) * 90), 90);
      setReadProgress(pct);
      if (step >= steps) clearInterval(ticker);
    }, INTERVAL_MS);

    const buffer = await f.arrayBuffer();
    clearInterval(ticker);
    setReadProgress(95);
    setProgressLabel("Computing SHA-256...");
    await new Promise((r) => setTimeout(r, 30));

    const hashBuffer = await window.crypto.subtle.digest("SHA-256", buffer);
    const hash = Array.from(new Uint8Array(hashBuffer))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
    setReadProgress(100);
    setSha256(hash);

    // Phase 2: Upload file to API
    setUploadProgress(0);
    setProgressLabel("Uploading...");
    await new Promise((r) => setTimeout(r, 30));
    try {
      const uploadResponse = await uploadFirmwareFile({
        file: f,
        fileName: f.name,
        onProgress: (pct) => setUploadProgress(pct),
      });
      firmwareIdRef.current = uploadResponse?.id || uploadResponse?.firmware_id || null;
      setUploadProgress(100);
      setUploadDone(true);
    } catch (err) {
      setUploadError(err.message || "Upload failed. Please try again.");
    } finally {
      setComputing(false);
    }
  }, []);

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f) processFile(f);
  };

  const uploadedAt = file
    ? new Date().toISOString().replace("T", " ").slice(0, 19) + " UTC"
    : null;

  return (
    <div className="space-y-5">
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        className={`rounded-2xl border-2 border-dashed cursor-pointer transition-colors py-16 px-8 flex flex-col items-center justify-center gap-3 ${
          dragOver ? "border-teal-400 bg-teal-50" : "border-slate-200 bg-slate-50/60 hover:border-teal-300"
        }`}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".bin"
          className="hidden"
          onChange={(e) => { if (e.target.files[0]) processFile(e.target.files[0]); }}
        />
        <div className="relative w-16 h-16">
          <div className="absolute inset-0 rounded-xl bg-indigo-100 flex items-end justify-center pb-1">
            <svg viewBox="0 0 40 32" className="w-10 h-8" fill="none">
              <rect x="2" y="14" width="36" height="16" rx="3" fill="#818cf8" />
              <rect x="8" y="18" width="24" height="8" rx="2" fill="#c7d2fe" />
            </svg>
          </div>
          <div className="absolute -top-2 left-1/2 -translate-x-1/2">
            <svg viewBox="0 0 24 28" className="w-6 h-7" fill="none">
              <path d="M12 26V6" stroke="#ef4444" strokeWidth="3" strokeLinecap="round" />
              <path d="M5 13l7-7 7 7" stroke="#ef4444" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        </div>
        <p className="text-base font-bold text-slate-700">Drag &amp; drop firmware file here</p>
        <p className="text-sm text-slate-400">or click to browse</p>
        <p className="text-sm font-medium text-teal-600">.bin files only &middot; Max 50 MB</p>
      </div>

      {computing && (
        <div className="bg-white border border-slate-200 rounded-xl px-5 py-4 space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-slate-700">Uploading...</p>
            <p className="text-sm font-bold text-indigo-600">
              {readProgress < 100 ? "Preparing..." : `${uploadProgress}%`}
            </p>
          </div>
          <div className="w-full h-2.5 bg-slate-100 rounded-full overflow-hidden">
            <div
              className="h-2.5 bg-indigo-500 rounded-full transition-all duration-300"
              style={{ width: readProgress < 100 ? "0%" : `${uploadProgress}%` }}
            />
          </div>
          <p className="text-xs text-slate-400">Do not close this window.</p>
        </div>
      )}

      {uploadError && (
        <div className="flex items-start gap-3 bg-red-50 border border-red-300 rounded-xl px-4 py-3">
          <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-semibold text-red-700">Upload Failed</p>
            <p className="text-xs text-red-500 mt-0.5">{uploadError}</p>
          </div>
        </div>
      )}

      {error && (
        <div className="flex items-start gap-3 bg-red-50 border border-red-300 rounded-xl px-4 py-3">
          <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-semibold text-red-700">Upload Rejected</p>
            <p className="text-xs text-red-500 mt-0.5">{error}</p>
          </div>
        </div>
      )}

      {(file || restoredFileInfo) && (
        <div className="bg-white rounded-xl border border-slate-200 px-5 py-4 flex items-start gap-4">
          <div className="w-11 h-11 rounded-xl bg-emerald-50 flex items-center justify-center text-2xl shrink-0">
            📦
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-slate-800 text-sm truncate">{file ? file.name : restoredFileInfo.name}</p>
            <p className="text-xs text-slate-400 mt-0.5">
              {formatBytes(file ? file.size : restoredFileInfo.size)} &middot; Uploaded {uploadedAt}
            </p>
            <p className="text-xs font-semibold text-teal-600 mt-2 mb-1">SHA-256</p>
            {computing ? (
              <p className="text-xs text-slate-400 italic">Computing...</p>
            ) : (
              <input
                readOnly
                value={sha256 || ""}
                className="w-full text-xs font-mono bg-slate-50 border border-slate-200 rounded px-2 py-1.5 text-slate-600 select-all"
              />
            )}
          </div>
          {sha256 && !computing && (
            <span className="text-emerald-500 text-xl mt-1 shrink-0">&#x2713;</span>
          )}
        </div>
      )}

      {sha256 && !computing && uploadDone && (
        <div className="flex items-start gap-3 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3">
          <CheckCircle2 className="w-5 h-5 text-emerald-500 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-semibold text-slate-800">File uploaded &middot; SHA-256 verified</p>
            <p className="text-xs text-slate-500 mt-0.5">File successfully uploaded to server. Proceed to metadata entry.</p>
          </div>
        </div>
      )}

      <div className="flex justify-end pt-2">
        <button
          disabled={(!sha256 && !restoredFileInfo) || computing || (!uploadDone && !restoredFileInfo)}
          onClick={() => onNext({ file, sha256, uploadedAt, firmwareId: firmwareIdRef.current, fileInfo: file ? { name: file.name, size: file.size } : restoredFileInfo })}
          className="flex items-center gap-2 bg-teal-800 hover:bg-teal-900 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-semibold px-6 py-2.5 rounded-lg transition-colors"
        >
          Next: Add Metadata
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

function StepMetadata({ uploadData, initialData, onNext, onBack }) {
  const reviewNotesInputRef = useRef();
  const [version, setVersion] = useState(initialData?.version || "");
  const [minFw, setMinFw] = useState(initialData?.minFw || "");
  const [reviewNoteFiles, setReviewNoteFiles] = useState([]);
  const [reviewNoteFileNames, setReviewNoteFileNames] = useState(initialData?.reviewNoteFileNames || []);
  const [mandatory, setMandatory] = useState(initialData?.mandatory ?? false);
  const [segment, setSegment] = useState(initialData?.segment || "");
  const [countries, setCountries] = useState(initialData?.countries || []);
  const [versionError, setVersionError] = useState(null);
  const [reviewNotesError, setReviewNotesError] = useState(null);
  const [countriesError, setCountriesError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [uploadedFileName, setUploadedFileName] = useState(uploadData?.file?.name || uploadData?.fileInfo?.name || null);
  const [uploadedAt, setUploadedAt] = useState(null);

  useEffect(() => {
    if (!uploadData?.firmwareId) return;
    getFirmwareFileInfo(uploadData.firmwareId)
      .then((info) => {
        const name = info?.file_name || info?.fileName || info?.name || null;
        if (name) setUploadedFileName(name);
        if (info?.uploaded_at) setUploadedAt(info.uploaded_at);
      })
      .catch(() => {});
  }, [uploadData?.firmwareId]);

  const allFileNames = reviewNoteFileNames;

  const ALLOWED_TYPES = [
    "application/pdf",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "text/plain",
  ];
  const ALLOWED_EXTS = [".pdf", ".docx", ".txt"];

  const isAllowed = (file) =>
    ALLOWED_TYPES.includes(file.type) ||
    ALLOWED_EXTS.some((ext) => file.name.toLowerCase().endsWith(ext));

  const addReviewNoteFiles = (incoming) => {
    const allFiles = Array.from(incoming);
    const rejected = allFiles.filter((f) => !isAllowed(f));
    const valid = allFiles.filter((f) => isAllowed(f) && !reviewNoteFileNames.includes(f.name));
    if (rejected.length > 0) {
      setReviewNotesError(`Only PDF, DOCX and TXT files are allowed. Rejected: ${rejected.map((f) => f.name).join(", ")}`);
    } else {
      setReviewNotesError(null);
    }
    if (valid.length === 0) return;
    setReviewNoteFiles((prev) => [...prev, ...valid]);
    setReviewNoteFileNames((prev) => [...prev, ...valid.map((f) => f.name)]);
  };

  const removeReviewNoteFile = (index) => {
    const nameToRemove = reviewNoteFileNames[index];
    setReviewNoteFiles((prev) => prev.filter((f) => f.name !== nameToRemove));
    setReviewNoteFileNames((prev) => prev.filter((_, i) => i !== index));
  };

  const validate = () => {
    let ok = true;
    if (!/^v?\d+\.\d+\.\d+(-[\w.]+)?$/.test(version.trim())) {
      setVersionError("Use semver format, e.g. v1.6.0 or v1.6.0-beta");
      ok = false;
    } else setVersionError(null);
    if (allFileNames.length === 0) {
      setReviewNotesError("Upload at least one review note file.");
      ok = false;
    } else setReviewNotesError(null);
    if (countries.length === 0) {
      setCountriesError("Select at least one target country.");
      ok = false;
    } else setCountriesError(null);
    return ok;
  };

  return (
    <div className="space-y-5">
      {/* Uploaded file banner */}
      {uploadedFileName && (
        <div className="flex items-center gap-3 bg-white border border-slate-200 rounded-xl px-4 py-3">
          <div className="w-8 h-8 rounded-lg bg-teal-50 border border-teal-200 flex items-center justify-center shrink-0">
            <svg className="w-4 h-4 text-teal-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <div className="min-w-0">
            <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest leading-none mb-0.5">Uploaded Firmware File</p>
            <p className="text-sm font-semibold text-slate-800 truncate">
              {uploadedFileName}
            </p>
          </div>
        </div>
      )}

      {/* White card */}
      <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-5">

        {/* Row 1: Version String + Min FW */}
        <div className="grid grid-cols-2 gap-5">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">
              Version String <span className="text-red-500">*</span>
            </label>
            <input
              value={version}
              onChange={(e) => setVersion(e.target.value)}
              placeholder="v1.6.0-beta"
              className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
            {versionError
              ? <p className="text-xs text-red-500 mt-1">{versionError}</p>
              : <p className="text-xs text-slate-400 mt-1">Semantic version &middot; must be unique &middot; e.g. v1.6.0</p>
            }
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">
              Minimum Device FW Required
            </label>
            <input
              value={minFw}
              onChange={(e) => setMinFw(e.target.value)}
              placeholder="v1.4.0 or later"
              className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
            <p className="text-xs text-slate-400 mt-1">Devices below this version cannot receive update</p>
          </div>
        </div>

        {/* Review Notes Upload */}
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1.5">
            Review Notes <span className="text-red-500">*</span>
          </label>
          <div
            onClick={() => reviewNotesInputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); addReviewNoteFiles(e.dataTransfer.files); }}
            className="border-2 border-dashed border-slate-200 rounded-xl px-5 py-4 flex flex-col items-center justify-center gap-2 cursor-pointer hover:border-teal-400 hover:bg-teal-50/30 transition-colors"
          >
            <input
              ref={reviewNotesInputRef}
              type="file"
              multiple
              accept=".pdf,.docx,.txt,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
              className="hidden"
              onChange={(e) => { if (e.target.files.length) addReviewNoteFiles(e.target.files); e.target.value = ""; }}
            />
            <svg className="w-7 h-7 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p className="text-sm text-slate-500">Drag &amp; drop files or <span className="text-teal-600 font-semibold">click to browse</span></p>
            <p className="text-xs text-slate-400">PDF, DOCX, TXT &middot; Multiple files allowed</p>
          </div>
          {allFileNames.length > 0 && (
            <ul className="mt-2 space-y-1.5">
              {allFileNames.map((name, i) => (
                <li key={i} className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <svg className="w-4 h-4 text-teal-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span className="text-sm text-slate-700 truncate">{name}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeReviewNoteFile(i)}
                    className="text-slate-400 hover:text-red-500 ml-3 shrink-0 transition-colors"
                  >
                    &times;
                  </button>
                </li>
              ))}
            </ul>
          )}
          {reviewNotesError && <p className="text-xs text-red-500 mt-1.5">{reviewNotesError}</p>}
        </div>

        {/* Row 2: Update Type + Target Segment */}
        <div className="grid grid-cols-2 gap-5">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">
              Update Type <span className="text-red-500">*</span>
            </label>
            <div className="border border-slate-200 rounded-lg px-4 py-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-slate-800">Mandatory Update</p>
                  <p className="text-xs text-slate-400">Forces update; all eligible devices required</p>
                </div>
                {/* Toggle */}
                <button
                  type="button"
                  onClick={() => setMandatory(!mandatory)}
                  className={`w-11 h-6 rounded-full transition-colors relative shrink-0 ml-3 ${mandatory ? "bg-teal-600" : "bg-slate-200"}`}
                >
                  <span className={`block w-4 h-4 bg-white rounded-full absolute top-1 shadow transition-transform ${mandatory ? "translate-x-6" : "translate-x-1"}`} />
                </button>
              </div>
            </div>
            <p className="text-xs text-slate-400 mt-1.5">Optional &mdash; clinician-initiated via Mobile App</p>
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">
              Target Segment
            </label>
            <input
              value={segment}
              onChange={(e) => setSegment(e.target.value)}
              placeholder="Canary (5%) &#x2014; initial rollout"
              className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
            <p className="text-xs text-slate-400 mt-1">Segment can be expanded after publish</p>
          </div>
        </div>

        {/* Target Countries */}
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1.5">
            Target Countries <span className="text-red-500">*</span>
          </label>
          <CountryDropdown
            value={countries}
            onChange={(val) => { setCountries(val); setCountriesError(null); }}
            error={!!countriesError}
          />
          {countriesError
            ? <p className="text-xs text-red-500 mt-1.5">{countriesError}</p>
            : <p className="text-xs text-slate-400 mt-1.5">
                {countries.length === 0
                  ? "Select all countries this firmware is approved for."
                  : `${countries.length} countr${countries.length === 1 ? "y" : "ies"} selected`}
              </p>
          }
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between pt-1">
        <button
          onClick={onBack}
          disabled={submitting}
          className="flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-800 border border-slate-200 rounded-lg px-4 py-2.5 bg-white transition-colors disabled:opacity-40"
        >
          &#8592; Back
        </button>
        {submitError && (
          <p className="text-xs text-red-500 self-center">{submitError}</p>
        )}
        <button
          disabled={submitting}
          onClick={async () => {
            if (!validate()) return;
            if (!uploadData?.firmwareId) {
              setSubmitError("Firmware ID is missing. Please go back to Step 1 and re-upload the file.");
              return;
            }
            setSubmitError(null);
            setSubmitting(true);
            try {
              // Convert each review note File to base64
              const release_notes_files = await Promise.all(
                reviewNoteFiles.map((f) =>
                  new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onload = () => resolve({
                      file_name: f.name,
                      file_data: reader.result.split(",")[1], // strip data:...;base64,
                    });
                    reader.onerror = reject;
                    reader.readAsDataURL(f);
                  })
                )
              );
              // Use freshly-converted files, or fall back to cached base64 from a previous pass
              const cachedReleaseNotesFiles = initialData?.cachedReleaseNotesFiles || [];
              const filesToSend = release_notes_files.length > 0 ? release_notes_files : cachedReleaseNotesFiles;
              const metaPayload = {
                version_string: version.trim(),
                min_device_fw_required: minFw || null,
                is_mandatory: mandatory,
                target_segment: segment || null,
                target_countries: countries.map((code) => COUNTRY_LABEL[code] || code),
                release_notes_files: filesToSend,
              };
              await patchFirmwareMetadata(uploadData.firmwareId, metaPayload);
              let firmwareRecord = null;
              try {
                firmwareRecord = await getBaseline(uploadData.firmwareId);
              } catch (_) {
                // GET failed — proceed without firmware record
              }
              // Persist the base64 file data in wizardData so it's available if the user navigates back
              const nextCachedFiles = release_notes_files.length > 0 ? release_notes_files : cachedReleaseNotesFiles;
              onNext({ ...uploadData, version: version.trim(), minFw, reviewNoteFileNames: allFileNames, mandatory, segment, countries, firmwareRecord, cachedReleaseNotesFiles: nextCachedFiles });
            } catch (err) {
              setSubmitError(err.message || "Failed to save metadata. Please try again.");
            } finally {
              setSubmitting(false);
            }
          }}
          className="flex items-center gap-2 bg-teal-800 hover:bg-teal-900 disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-semibold px-6 py-2.5 rounded-lg transition-colors"
        >
          {submitting ? "Saving..." : "Next: Review & Sign"}
          {!submitting && <ChevronRight className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}

function StepReview({ data, onNext, onBack }) {
  const { user, role } = useAuth();
  const [kmsVerified, setKmsVerified] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [viewing, setViewing] = useState(false);
  const [hexContent, setHexContent] = useState(null);
  const [rnLoading, setRnLoading] = useState(null);    // key of file being actioned
  const [rnViewUrl, setRnViewUrl] = useState(null);     // URL shown in side panel
  const [rnViewName, setRnViewName] = useState(null);   // filename shown in panel header
  const [rnViewIndex, setRnViewIndex] = useState(null); // index of currently viewed file
  const [uploadedFileName, setUploadedFileName] = useState(data?.file?.name || data?.fileInfo?.name || null);
  const [uploadedAt, setUploadedAt] = useState(null);

  useEffect(() => {
    if (!data?.firmwareId) return;
    getFirmwareFileInfo(data.firmwareId)
      .then((info) => {
        const name = info?.file_name || info?.fileName || info?.name || null;
        if (name) setUploadedFileName(name);
        if (info?.uploaded_at) setUploadedAt(info.uploaded_at);
      })
      .catch(() => {});
  }, [data?.firmwareId]);

  const fetchBinBlob = async () => {
    const token = await getAuthToken();
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const res = await fetch(getFirmwareBinUrl(data.firmwareId), { headers });
    if (!res.ok) throw new Error(`Failed: ${res.status}`);
    return res.blob();
  };

  const handleDownloadBin = async () => {
    if (!data.firmwareId) return;
    setDownloading(true);
    try {
      const blob = await fetchBinBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = data.fileInfo?.name || data.file?.name || `firmware-${data.firmwareId}.bin`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("[downloadBin]", err);
    } finally {
      setDownloading(false);
    }
  };

  const handleViewBin = async () => {
    if (!data.firmwareId) return;
    setViewing(true);
    try {
      const blob = await fetchBinBlob();
      const buffer = await blob.arrayBuffer();
      const bytes = new Uint8Array(buffer);
      // Build hex dump: 16 bytes per row
      const lines = [];
      for (let i = 0; i < Math.min(bytes.length, 4096); i += 16) {
        const slice = bytes.slice(i, i + 16);
        const offset = i.toString(16).padStart(8, "0").toUpperCase();
        const hex = Array.from(slice).map((b) => b.toString(16).padStart(2, "0").toUpperCase()).join(" ");
        const ascii = Array.from(slice).map((b) => (b >= 32 && b < 127 ? String.fromCharCode(b) : "." )).join("");
        lines.push(`${offset}  ${hex.padEnd(47)}  ${ascii}`);
      }
      if (bytes.length > 4096) lines.push(`\n... (showing first 4 KB of ${(bytes.length / 1024).toFixed(1)} KB)`);
      setHexContent(lines.join("\n"));
    } catch (err) {
      console.error("[viewBin]", err);
    } finally {
      setViewing(false);
    }
  };

  const releaseNoteFiles = data.firmwareRecord?.release_notes_files || data.reviewNoteFileNames?.map((name) => ({ file_name: name })) || [];

  // Resolves the API metadata response to the actual presigned file URL.
  // The endpoint returns JSON like { release_notes: [{ file_name, download_url }] }.
  const resolveReleaseNoteUrl = async (index) => {
    const token = await getAuthToken();
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const metaUrl = `${getFirmwareReleaseNotesUrl(data.firmwareId)}&index=${index}`;
    const res = await fetch(metaUrl, { headers });
    if (!res.ok) throw new Error(`Failed: ${res.status}`);
    const contentType = res.headers.get("content-type") || "";
    if (contentType.includes("application/json")) {
      const json = await res.json();
      const presigned =
        json.release_notes?.[index]?.download_url ||
        json.release_notes?.[0]?.download_url ||
        json.download_url ||
        null;
      if (presigned) return { type: "presigned", url: presigned };
      // Fallback: render the JSON as plain text
      const blob = new Blob([JSON.stringify(json, null, 2)], { type: "text/plain" });
      return { type: "blob", url: URL.createObjectURL(blob) };
    }
    const blob = await res.blob();
    return { type: "blob", url: URL.createObjectURL(blob) };
  };

  const handleDownloadReleaseNote = async (index) => {
    setRnLoading(`dl-${index}`);
    try {
      const { type, url: fileUrl } = await resolveReleaseNoteUrl(index);
      if (type === "presigned") {
        // Fetch the actual file from the presigned URL so we can name it correctly
        const fileRes = await fetch(fileUrl);
        if (!fileRes.ok) throw new Error(`File download failed: ${fileRes.status}`);
        const blob = await fileRes.blob();
        const objectUrl = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = objectUrl;
        a.download = releaseNoteFiles[index]?.file_name || `release-note-${index}`;
        a.click();
        setTimeout(() => URL.revokeObjectURL(objectUrl), 5000);
      } else {
        const a = document.createElement("a");
        a.href = fileUrl;
        a.download = releaseNoteFiles[index]?.file_name || `release-note-${index}`;
        a.click();
        setTimeout(() => URL.revokeObjectURL(fileUrl), 5000);
      }
    } catch (err) {
      console.error("[downloadReleaseNote]", err);
    } finally {
      setRnLoading(null);
    }
  };

  const handleViewReleaseNote = async (index) => {
    // Toggle: clicking the same open file closes the panel
    if (rnViewIndex === index && rnViewUrl) {
      handleCloseRnPanel();
      return;
    }
    setRnLoading(`view-${index}`);
    try {
      const { type, url: fileUrl } = await resolveReleaseNoteUrl(index);
      if (rnViewUrl && rnViewUrl.startsWith("blob:")) URL.revokeObjectURL(rnViewUrl);
      setRnViewUrl(fileUrl);
      setRnViewName(releaseNoteFiles[index]?.file_name || `Release Note ${index + 1}`);
      setRnViewIndex(index);
    } catch (err) {
      console.error("[viewReleaseNote]", err);
    } finally {
      setRnLoading(null);
    }
  };

  const handleCloseRnPanel = () => {
    if (rnViewUrl && rnViewUrl.startsWith("blob:")) URL.revokeObjectURL(rnViewUrl);
    setRnViewUrl(null);
    setRnViewName(null);
    setRnViewIndex(null);
  };

  // Simulate KMS signing on mount
  useEffect(() => {
    const t = setTimeout(() => setKmsVerified(true), 900);
    return () => clearTimeout(t);
  }, []);

  const reviewerLine = (() => {
    const name = user?.name || "Unknown";
    const initials = name.split(" ").map((p) => p[0]).join("");
    const displayName = name.split(" ")[0] + " " + (name.split(" ")[1]?.[0] || "") + ".";
    const roleLabel = role === "FleetAdmin" ? "Fleet Admin" : role === "ReleaseManager" ? "Fleet Admin" : role || "";
    const now = new Date();
    const dateStr = now.toISOString().slice(0, 10) + " " + now.toTimeString().slice(0, 5) + " UTC";
    return `I have reviewed and verified the above details \u2014 ${displayName} \u00b7 ${roleLabel} \u00b7 ${dateStr}`;
  })();

  const rows = [
    { label: "Version",        value: data.version, bold: true },
    { label: "File Name",      value: data.file?.name, teal: true },
    { label: "File Size",      value: data.file ? `${formatBytes(data.file.size)} (${data.file.size.toLocaleString()} bytes)` : "\u2014" },
    { label: "Update Type",    value: null, pill: data.mandatory ? "Mandatory" : "Optional", mandatory: data.mandatory },
    { label: "Min Device FW",  value: data.minFw || "\u2014" },
    { label: "Target Segment", value: data.segment || "\u2014" },
    { label: "Countries",      value: (() => { const codes = data.firmwareRecord?.target_countries || data.countries || []; return codes.length > 0 ? codes.map((c) => COUNTRY_LABEL[c] || c).join(", ") : "\u2014"; })() },
    { label: "SHA-256 Hash",   value: data.sha256, mono: true },
  ];

  return (
    <div className={`flex gap-5 items-start transition-all duration-300 ${rnViewUrl ? "w-[calc(100vw-16rem-4rem)] -mr-[9999px]" : ""}`}>
      {/* Left: main content column */}
      <div className={`${rnViewUrl ? "w-[42rem] shrink-0" : "w-full"} space-y-4 min-w-0`}>
      {/* Uploaded firmware file banner */}
      {uploadedFileName && (
        <div className="flex items-center gap-3 bg-white border border-slate-200 rounded-xl px-4 py-3">
          <div className="w-8 h-8 rounded-lg bg-teal-50 border border-teal-200 flex items-center justify-center shrink-0">
            <svg className="w-4 h-4 text-teal-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <div className="min-w-0">
            <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest leading-none mb-0.5">Uploaded Firmware File</p>
            <p className="text-sm font-semibold text-slate-800 truncate">
              {uploadedFileName}
            </p>
          </div>
        </div>
      )}

      {/* Hex viewer modal */}
      {hexContent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-2xl w-[90vw] max-w-4xl flex flex-col" style={{ maxHeight: "85vh" }}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
              <div>
                <p className="text-sm font-bold text-slate-800">{data.fileInfo?.name || data.file?.name || "firmware.bin"}</p>
                <p className="text-xs text-slate-400">Hex dump &middot; first 4 KB shown</p>
              </div>
              <button
                onClick={() => setHexContent(null)}
                className="text-slate-400 hover:text-slate-700 text-xl font-bold px-2"
              >&times;</button>
            </div>
            <pre className="overflow-auto flex-1 text-xs font-mono bg-slate-950 text-green-400 p-4 rounded-b-xl">{hexContent}</pre>
          </div>
        </div>
      )}

      {/* Firmware Details card */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-6 py-3 border-b border-slate-100">
          <span className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">Firmware Details</span>
        </div>
        <div className="divide-y divide-slate-100">
          {rows.map(({ label, value, bold, teal, mono, pill, mandatory: isMandatory }) => (
            <div key={label} className="flex items-start justify-between px-6 py-3 gap-6">
              <span className="text-sm font-medium text-slate-400 shrink-0 w-36">{label}</span>
              {label === "File Name" ? (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-teal-600 font-medium break-all text-right">{value || "\u2014"}</span>
                  <button
                    type="button"
                    onClick={handleDownloadBin}
                    disabled={downloading || viewing}
                    className="shrink-0 flex items-center gap-1 text-xs font-semibold text-teal-700 border border-teal-300 bg-teal-50 hover:bg-teal-100 px-2.5 py-1 rounded-lg transition-colors disabled:opacity-50"
                  >
                    {downloading ? "..." : "\u2193 Download"}
                  </button>
                  {/* <button
                    type="button"
                    onClick={handleViewBin}
                    disabled={viewing || downloading}
                    className="shrink-0 flex items-center gap-1 text-xs font-semibold text-slate-600 border border-slate-300 bg-white hover:bg-slate-50 px-2.5 py-1 rounded-lg transition-colors disabled:opacity-50"
                  >
                    {viewing ? "..." : "\u{1F441} View"}
                  </button> */}
                </div>
              ) : (
              <span className={`text-sm text-right break-all ${
                bold ? "font-bold text-slate-800" :
                teal ? "text-teal-600 font-medium" :
                mono ? "font-mono text-slate-600 text-xs leading-5" :
                "text-slate-700"
              }`}>
                {pill ? (
                  <span className={`inline-block text-xs px-3 py-0.5 rounded-full border ${
                    isMandatory
                      ? "border-red-300 text-red-600 bg-red-50"
                      : "border-slate-300 text-slate-600"
                  }`}>{pill}</span>
                ) : (value || "\u2014")}
              </span>
              )}
            </div>
          ))}
        </div>
        {/* Release Notes files — same row style as File Name */}
        {releaseNoteFiles.length > 0 && (
          <div className="divide-y divide-slate-100 border-t border-slate-100">
            {releaseNoteFiles.map((f, i) => (
              <div key={i} className="flex items-center justify-between px-6 py-3 gap-3 min-w-0">
                <span className="text-sm font-medium text-slate-400 shrink-0 w-28">{i === 0 ? "Release Notes" : ""}</span>
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <span className="text-sm text-teal-600 font-medium truncate" title={f.file_name || `File ${i + 1}`}>{f.file_name || `File ${i + 1}`}</span>
                  <button
                    type="button"
                    onClick={() => handleDownloadReleaseNote(i)}
                    disabled={rnLoading !== null}
                    className="shrink-0 flex items-center gap-1 text-xs font-semibold text-teal-700 border border-teal-300 bg-teal-50 hover:bg-teal-100 px-2.5 py-1 rounded-lg transition-colors disabled:opacity-50"
                  >
                    {rnLoading === `dl-${i}` ? "..." : "\u2193 Download"}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleViewReleaseNote(i)}
                    disabled={rnLoading !== null}
                    className={`shrink-0 flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-lg border transition-colors disabled:opacity-50 ${
                      rnViewIndex === i
                        ? "text-teal-700 border-teal-400 bg-teal-50 hover:bg-teal-100"
                        : "text-slate-600 border-slate-300 bg-white hover:bg-slate-50"
                    }`}
                  >
                    {rnLoading === `view-${i}` ? "..." : rnViewIndex === i ? "\u{1F441} Viewing" : "\u{1F441} View"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* KMS Signature banner */}
      {kmsVerified ? (
        <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3">
          <span className="text-xl">&#x1F512;</span>
          <div>
            <p className="text-sm font-bold text-teal-800">KMS Asymmetric Signature \u2014 Verified</p>
            <p className="text-xs text-slate-500 mt-0.5">
              Key ID: arn:aws:kms:us-east-1:123456789:key/mrk-abc123 &middot; Algorithm: RSASSA_PKCS1_V1_5_SHA_256
            </p>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-xl px-4 py-3">
          <span className="text-xl animate-spin">&#8987;</span>
          <p className="text-sm text-slate-500">Requesting KMS signature...</p>
        </div>
      )}

      {/* SaMD Review Gate */}
      <div className="bg-amber-50 border border-amber-300 rounded-xl px-5 py-4 space-y-3">
        <p className="text-xs font-bold text-amber-800 uppercase tracking-wider">&#8225; Mandatory Review Gate</p>
        <p className="text-sm text-slate-700">
          I confirm that I have reviewed all firmware details above, verified the SHA-256 hash matches the source file, and
          authorise this baseline for publication to the Tetra fleet. This action is immutably audit-logged per IEC 62304.
        </p>
        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
            className="hidden"
          />
          <div className={`w-5 h-5 rounded flex items-center justify-center shrink-0 transition-colors ${
            confirmed ? "bg-amber-600 border-amber-600" : "border-2 border-amber-400 bg-white"
          }`}>
            {confirmed && <CheckCircle2 className="w-3.5 h-3.5 text-white" />}
          </div>
          <span className="text-sm font-semibold text-slate-700">{reviewerLine}</span>
        </label>
      </div>

      {/* Navigation */}
      <div className="flex justify-between pt-1">
        <button
          onClick={onBack}
          className="flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-800 border border-slate-200 rounded-lg px-4 py-2.5 bg-white transition-colors"
        >
          &#8592; Back
        </button>
        <button
          disabled={!confirmed || !kmsVerified}
          onClick={() => onNext(data)}
          className="flex items-center gap-2 bg-teal-800 hover:bg-teal-900 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-semibold px-6 py-2.5 rounded-lg transition-colors"
        >
          Proceed to Publish &#8594;
        </button>
      </div>
      </div>{/* end left column */}

      {/* Right: release note viewer panel */}
      {rnViewUrl && (
        <div className="flex-1 min-w-0 sticky top-4 flex flex-col" style={{ height: "calc(100vh - 10rem)" }}>
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden flex flex-col h-full shadow-sm">
            {/* Panel header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-slate-50 shrink-0">
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-base leading-none shrink-0">&#x1F4C4;</span>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-slate-800 truncate leading-tight">{rnViewName}</p>
                  <p className="text-[11px] text-slate-400 mt-0.5">Release Note &middot; Preview</p>
                </div>
              </div>
              <button
                type="button"
                onClick={handleCloseRnPanel}
                className="ml-3 shrink-0 w-6 h-6 flex items-center justify-center rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-200 transition-colors text-base font-bold leading-none"
                aria-label="Close viewer"
              >&times;</button>
            </div>
            <iframe
              src={rnViewUrl}
              title={rnViewName}
              className="flex-1 w-full border-0"
            />
          </div>
        </div>
      )}
    </div>
  );
}

const SEGMENTS = [
  {
    id: "canary",
    emoji: "🌱",
    label: "Canary",
    desc: "Initial rollout — monitor failure rate before expanding",
    devices: "~12 devices (5% fleet)",
    pct: 5,
    approx: 12,
  },
  {
    id: "extended",
    emoji: "🔵",
    label: "Extended",
    desc: "Expand after canary success threshold met (F07b <2%)",
    devices: "~62 devices (25% fleet)",
    pct: 25,
    approx: 62,
  },
  {
    id: "full",
    emoji: "🌐",
    label: "Full Fleet",
    desc: "All eligible devices · requires canary + extended pass",
    devices: "247 devices (100%)",
    pct: 100,
    approx: 247,
  },
];

function StepPublish({ data, onBack, onDone }) {
  const [selectedSeg, setSelectedSeg] = useState("canary");
  const [publishing, setPublishing] = useState(false);
  const [done, setDone] = useState(false);
  const { toast } = useToast();

  const seg = SEGMENTS.find((s) => s.id === selectedSeg);

  const configRows = [
    { label: "Version",           value: data.version },
    { label: "Segment",           value: `${seg.label} \u2014 ${seg.pct}% (\u2248${seg.approx} devices)` },
    { label: "Notification",      value: "SNS \u2192 APNs/FCM push to Clinicians in segment" },
    { label: "Rollout Pauses If", value: "F07b failure rate >5% at canary stage" },
    { label: "CloudFront URL TTL",value: "10 min (signed URL \u00b7 pre-minted on download request)", mono: true },
  ];

  const handlePublish = async () => {
    setPublishing(true);
    try {
      setDone(true);
    } catch {
      toast({ title: "Publish failed", description: "Could not publish the baseline.", variant: "destructive" });
    } finally {
      setPublishing(false);
    }
  };

  if (done) {
    return (
      <div className="flex flex-col items-center py-16 gap-4">
        <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center">
          <CheckCircle2 className="w-9 h-9 text-emerald-500" />
        </div>
        <h2 className="text-xl font-bold text-slate-800">Baseline Published!</h2>
        <p className="text-sm text-slate-500">
          Version <strong>{data.version}</strong> published to the{" "}
          <strong>{seg.label}</strong> segment.
        </p>
        <button
          onClick={onDone}
          className="mt-4 bg-teal-800 hover:bg-teal-900 text-white text-sm font-semibold px-8 py-2.5 rounded-lg transition-colors"
        >
          Back to Baseline Library
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Segment selector cards */}
      <div className="grid grid-cols-3 gap-4">
        {SEGMENTS.map((s) => (
          <button
            key={s.id}
            onClick={() => setSelectedSeg(s.id)}
            className={`text-left rounded-xl border-2 px-4 py-4 transition-colors ${
              selectedSeg === s.id
                ? "border-teal-500 bg-teal-50"
                : "border-slate-200 bg-white hover:border-teal-300"
            }`}
          >
            <p className="text-sm font-bold text-slate-800 mb-1">
              <span className="mr-1.5">{s.emoji}</span>{s.label}
            </p>
            <p className="text-xs text-slate-500 mb-3 leading-snug">{s.desc}</p>
            <p className="text-xs font-semibold text-teal-700">{s.devices}</p>
          </button>
        ))}
      </div>

      {/* Publish configuration */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-6 py-3 border-b border-slate-100">
          <span className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">Publish Configuration</span>
        </div>
        <div className="divide-y divide-slate-100">
          {configRows.map(({ label, value, mono }) => (
            <div key={label} className="flex items-start justify-between px-6 py-3 gap-6">
              <span className="text-sm font-medium text-slate-400 shrink-0 w-40">{label}</span>
              <span className={`text-sm text-right break-all ${mono ? "font-mono text-teal-600 text-xs" : "text-slate-700"}`}>
                {value}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Info banner */}
      <div className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3">
        <span className="text-blue-500 text-lg leading-none mt-0.5">&#x2139;</span>
        <div>
          <p className="text-sm font-semibold text-slate-800">Step Functions phased rollout \u2014 automatic pause on failure</p>
          <p className="text-xs text-slate-500 mt-0.5">
            Canary \u2192 watch F07b rate \u2192 25% \u2192 100%. Failure threshold pauses rollout automatically. This action is final and immutably logged.
          </p>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between pt-1">
        <button
          onClick={onBack}
          disabled={publishing}
          className="flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-800 border border-slate-200 rounded-lg px-4 py-2.5 bg-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          &#8592; Back
        </button>
        <button
          disabled={publishing}
          onClick={handlePublish}
          className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-semibold px-6 py-2.5 rounded-lg transition-colors"
        >
          &#x1F680; Publish to {seg.label} Segment
        </button>
      </div>
    </div>
  );
}

export default function UploadBaselinePage() {
  const navigate = useNavigate();

  // On fresh mount, always start from step 1 with no data.
  // sessionStorage is only used to survive an accidental in-session page refresh.
  // It is cleared here so that navigating away and back always gives a clean wizard.
  const [step, setStep] = useState(() => {
    try {
      const s = Number(sessionStorage.getItem("uploadWizard_step"));
      // Only restore if we have actual wizard data saved (not a stale entry)
      const saved = sessionStorage.getItem("uploadWizard_data");
      return s && saved ? s : 1;
    } catch { return 1; }
  });
  const [wizardData, setWizardData] = useState(() => {
    try {
      const saved = sessionStorage.getItem("uploadWizard_data");
      return saved ? JSON.parse(saved) : {};
    } catch { return {}; }
  });

  // Clear sessionStorage when the component unmounts (user navigates away)
  useEffect(() => {
    return () => {
      try {
        sessionStorage.removeItem("uploadWizard_step");
        sessionStorage.removeItem("uploadWizard_data");
      } catch {}
    };
  }, []);

  const updateWizardData = (data) => {
    setWizardData(data);
    try {
      sessionStorage.setItem("uploadWizard_data", JSON.stringify(data, (k, v) => v instanceof File ? undefined : v));
    } catch {}
  };

  const updateStep = (s) => {
    setStep(s);
    try { sessionStorage.setItem("uploadWizard_step", String(s)); } catch {}
  };

  const clearWizard = () => {
    setStep(1);
    setWizardData({});
    try {
      sessionStorage.removeItem("uploadWizard_step");
      sessionStorage.removeItem("uploadWizard_data");
    } catch {}
  };

  const subtitles = [
    "Upload the signed firmware binary file (.bin)",
    "Enter version, release notes and update type",
    "Verify all details before KMS signing \u00b7 SaMD mandatory review gate",
    "Select rollout segment \u00b7 Push notification sent via SNS \u2192 APNs/FCM",
  ];

  return (
    <div className="max-w-3xl">
      <div className="flex items-center gap-5 mb-2">
        <button
          onClick={() => step === 1 ? navigate("/firmware") : updateStep(step - 1)}
          className="flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-800 border border-slate-200 rounded-lg px-3 py-2 bg-white transition-colors shrink-0"
        >
          &#8592; Back
        </button>
        <h1 className="text-2xl font-bold text-slate-900">
          {step === 1 && "New Baseline \u2014 Upload Firmware"}
          {step === 2 && "New Baseline \u2014 Metadata"}
          {step === 3 && "Review & Sign"}
          {step === 4 && `Publish ${wizardData.version || ""}`}
        </h1>
      </div>
      <p className="text-sm text-slate-400 mb-8">
        Step {step} of 4 &middot; {subtitles[step - 1]}
      </p>

      <StepIndicator current={step} />

      {step === 1 && (
        <StepUpload
          initialFile={wizardData.file}
          initialSha256={wizardData.sha256}
          initialFileInfo={wizardData.fileInfo}
          initialFirmwareId={wizardData.firmwareId}
          onNext={(data) => { updateWizardData(data); updateStep(2); }}
        />
      )}
      {step === 2 && (
        <StepMetadata
          uploadData={wizardData}
          initialData={wizardData}
          onBack={() => updateStep(1)}
          onNext={(data) => { updateWizardData(data); updateStep(3); }}
        />
      )}
      {step === 3 && (
        <StepReview
          data={wizardData}
          onBack={() => updateStep(2)}
          onNext={(data) => { updateWizardData(data); updateStep(4); }}
        />
      )}
      {step === 4 && (
        <StepPublish
          data={wizardData}
          onBack={() => updateStep(3)}
          onDone={() => { clearWizard(); navigate("/firmware"); }}
        />
      )}
    </div>
  );
}